import { TOURNAMENT_STATUS } from '../../ScheduleBracket/constants'

/**
 * Decides whether a tournament division's Game Slots tab can be saved.
 *
 * Save is not a passive "persist my edits" button on this tab: the PUT it
 * issues is the only path that turns game slots into `game` rows, and the same
 * request promotes the division off Unconfigured. Gating it on the unsaved-edit
 * buffer meant "Save unlocks only if you edit every row in this sitting", so a
 * division whose slots were already complete on the server — filled by a CSV
 * import, or by a previous session — could never be saved at all.
 *
 * The predicate therefore reads the fetched server rows and overlays pending
 * edits on top, and requires a venue and a start time on every slot that is
 * not a bye.
 *
 * Facility is deliberately NOT required, and this is the part that keeps
 * getting flipped — read this before changing REQUIRED_KEYS again.
 * GameSlotsService::createGamesForGameSlots skips any slot with an empty
 * facility_id, so on paper the client should demand one. It must not: a
 * facility is optional on a location, and customers run whole competitions on
 * locations that have none (UPSL: 323 of 349 locations have zero facilities).
 * Requiring it here is SN-7991 — "Can't create enhanced playoffs for locations
 * without facilities" — and greys Save out for those customers permanently.
 *
 * So client and server disagree on purpose, and the client is the looser of
 * the two. The consequence is real and is not this file's to fix: for a
 * facility-less slot Save succeeds, the backend creates no game, and
 * GameSlotController::updateBulk promotes the division anyway. The fix is to
 * drop facility_id from the backend guard (game.facility_id has been nullable
 * since 2018 and the game resources already null-guard it) — ticketed
 * separately. When that lands, this comment can go.
 *
 * Byes are deliberately excluded. TournamentBuilder::createGameSlot creates
 * them with no venue, facility or start time, they are returned by
 * GET /gameslots like any other row, and the backend never gives them a game.
 * Requiring them to be filled would pin Save disabled forever on every bracket
 * with an odd team count.
 */

/**
 * The fields a slot must have before Save is offered. Intentionally two, not
 * the backend's three — see the note on facility above.
 */
const REQUIRED_KEYS = ['venue_id', 'starts_at'] as const

type RequiredKey = (typeof REQUIRED_KEYS)[number]

/** A game slot row as returned by GET /gameslots (SimpleGameSlotResource). */
export type GameSlotRow = {
  /** Slot uid — the same key the edit buffer is keyed on. */
  id: string
  has_bye?: boolean
  venue_id?: string | null
  facility_id?: string | null
  starts_at?: string | null
}

/** Pending, unsaved edits for one row. Extra keys (e.g. the TBD flag) are carried through untouched. */
export type GameSlotFormEdits = {
  venue_id?: string | null
  facility_id?: string | null
  starts_at?: string | null
  [key: string]: unknown
}

/** The whole edit buffer, keyed by slot uid. */
export type GameSlotForms = Record<string, GameSlotFormEdits | undefined>

export type DivisionSaveableInput = {
  games: GameSlotRow[] | null | undefined
  forms: GameSlotForms | null | undefined
  tournamentStatus: number | null | undefined
}

/**
 * Statuses where the division has already been configured and its games exist.
 * Editing there is row-by-row, so a half-filled row is a legitimate
 * work-in-progress rather than a blocker. Sync Teams also keys off this branch
 * staying permissive.
 */
const ALREADY_CONFIGURED_STATUSES: number[] = [
  TOURNAMENT_STATUS.PRE_PLACEMENT,
  TOURNAMENT_STATUS.FULL_PLACEMENT,
  TOURNAMENT_STATUS.IN_PROGRESS,
]

/** A value counts as filled unless it is absent or an empty/blank string. */
const hasValue = (value: unknown): boolean => {
  if (typeof value === 'string') {
    return value.trim().length > 0
  }
  return value !== null && value !== undefined
}

/**
 * What the row is actually showing: the pending edit when the user has touched
 * that field, otherwise the server value. `hasOwnProperty` rather than a
 * truthiness check, so a field the user deliberately cleared to '' stays
 * cleared — the same rule GameSlot.jsx renders by.
 */
const effectiveSlotValue = (
  slot: GameSlotRow,
  forms: GameSlotForms,
  key: RequiredKey
): unknown => {
  const edits = forms[slot.id]
  if (edits && Object.prototype.hasOwnProperty.call(edits, key)) {
    return edits[key]
  }
  return slot[key]
}

export const isDivisionSaveable = ({
  games,
  forms,
  tournamentStatus,
}: DivisionSaveableInput): boolean => {
  // Nothing fetched yet, or a division with no bracket: there is no game to
  // create, so there is nothing to save.
  if (!games?.length) {
    return false
  }

  if (
    typeof tournamentStatus === 'number' &&
    ALREADY_CONFIGURED_STATUSES.includes(tournamentStatus)
  ) {
    return true
  }

  const editBuffer = forms ?? {}
  const requiredSlots = games.filter((slot) => !slot.has_bye)

  // A bracket of nothing but byes would promote to a division with zero games.
  if (requiredSlots.length === 0) {
    return false
  }

  return requiredSlots.every((slot) =>
    REQUIRED_KEYS.every((key) =>
      hasValue(effectiveSlotValue(slot, editBuffer, key))
    )
  )
}
